<script setup lang="ts">
import RadButton from '../atoms/RadButton.vue';
import RadIcon from '../atoms/RadIcon.vue';

interface Props {
  title: string;
  subtitle: string;
  badgeText?: string;
  primaryCtaText?: string;
  secondaryCtaText?: string;
}

withDefaults(defineProps<Props>(), {
  primaryCtaText: 'Get Started',
  secondaryCtaText: 'Documentation',
});

defineEmits<{
  (e: 'primaryClick'): void;
  (e: 'secondaryClick'): void;
}>();
</script>

<template>
  <section class="relative pt-32 pb-20 px-4 overflow-hidden bg-white dark:bg-slate-950">
    <!-- Background Glows -->
    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-6xl h-[500px] bg-red/10 blur-[120px] rounded-full -z-10"></div>
    <div class="absolute bottom-0 right-0 w-[400px] h-[400px] bg-rad-blue/10 blur-[100px] rounded-full -z-10"></div>

    <div class="max-w-7xl mx-auto text-center">
      <!-- Badge -->
      <div v-if="badgeText" class="inline-flex items-center gap-2 px-4 py-2 bg-red/10 border border-white rounded-full mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <RadIcon name="Workflow" :size="16" class="text-white" />
        <span class="text-sm font-medium text-white">{{ badgeText }}</span>
      </div>

      <!-- Headline -->
      <h1 class="text-5xl sm:text-7xl font-bold mb-6 tracking-tight text-slate-900 dark:text-rad-white animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100 leading-[1.1] pb-2">
        {{ title }}
        <span class="block mt-2 rad-text-gradient py-1">
          Agentic Applications
        </span>
      </h1>

      <!-- Subheadline -->
      <p class="text-xl text-slate-700 dark:text-slate-400 mb-10 max-w-3xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
        {{ subtitle }}
      </p>

      <!-- CTA Buttons -->
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300">
        <RadButton @click="$emit('primaryClick')" size="lg" icon="ArrowRight">
          {{ primaryCtaText }}
        </RadButton>
        <!--
        <RadButton @click="$emit('secondaryClick')" variant="outline" size="lg">
          {{ secondaryCtaText }}
        </RadButton>
        -->
      </div>

      <!-- Hero Visual -->
      <div class="relative max-w-5xl mx-auto animate-in fade-in zoom-in duration-1000 delay-500">
        <div class="absolute inset-0 bg-rad-gradient opacity-20 blur-3xl -z-10"></div>
        <div class="rad-glass rounded-2xl p-1 border border-white/10 dark:border-white/10 shadow-2xl">
          <div class="bg-white dark:bg-slate-950 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
            <div class="h-8 bg-slate-100 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center px-4 gap-2">
              <div class="w-2 h-2 rounded-full bg-red-500/50"></div>
              <div class="w-2 h-2 rounded-full bg-amber-500/50"></div>
              <div class="w-2 h-2 rounded-full bg-emerald-500/50"></div>
              <div class="ml-4 text-[10px] text-slate-500 font-mono">radsuite-os --v1.0.0</div>
            </div>
            <div class="p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div v-for="i in 3" :key="i" class="p-6 rounded-xl bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 hover:border-primary/30 transition-colors group text-left">
                <div class="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <RadIcon :name="['Zap', 'Bot', 'Shield'][i-1]" :size="24" class="text-primary" />
                </div>
                <h3 class="text-slate-900 dark:text-rad-white font-semibold mb-2">{{ ['Build', 'Deploy', 'Scale'][i-1] }}</h3>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  {{ [
                    'Rapidly assemble or edit agentic applications with a collaborative development environment.',
                    'Deploy applications to staging and production environments with supervised workflows.',
                    'Manage agentic applications with lifecycle controls and operational visibility.'
                  ][i-1] }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
